import { languageQuery } from '@gig/magic-common';
import { LANGUAGE_UPDATED, FEATURE_QUERY_VALUE, FEATURE_QUERY_ENABLED, FEATURE_QUERY_ACTIVE, FEATURE_UPDATED } from '@gig/magic-messages';
import { magic } from '@gig/magic-sdk';

var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
class FeatureService {
  constructor(countryService, deviceService) {
    this.countryService = countryService;
    this.deviceService = deviceService;
    this.registerEvents();
    Promise.all([languageQuery(), this.getFeatures()]).then(([language]) => {
      this.language = language;
      this.registerQueryHandlers();
    });
    window.eventBus.subscribe(LANGUAGE_UPDATED, (event) => {
      this.language = event.payload;
    });
  }
  registerQueryHandlers() {
    window.queryBus.subscribe(FEATURE_QUERY_VALUE, (event) => __async(this, null, function* () {
      var _a;
      if (!((_a = event.payload) == null ? void 0 : _a.slug)) {
        return;
      }
      return this.getFeatureValue(event.payload);
    }));
    window.queryBus.subscribe(FEATURE_QUERY_ENABLED, (event) => __async(this, null, function* () {
      if (!event.payload) {
        return false;
      }
      return this.isFeatureEnabled(event.payload);
    }));
    window.queryBus.subscribe(FEATURE_QUERY_ACTIVE, (event) => __async(this, null, function* () {
      if (!event.payload) {
        return false;
      }
      return this.isFeatureActive(event.payload);
    }));
  }
  checkFeatureOverride() {
    const overrideValues = window.eventBus.getLatest(FEATURE_UPDATED);
    if (overrideValues) {
      this.overrideFeaturesFromQuery(overrideValues);
    }
    window.eventBus.subscribe(FEATURE_UPDATED, (e) => {
      if (e.payload && this.features) {
        this.overrideFeaturesFromQuery(e.payload);
      }
    });
  }
  registerEvents() {
    window.queryBus.register(FEATURE_QUERY_VALUE, {
      type: "object",
      required: ["slug"],
      properties: {
        slug: {
          type: "string"
        },
        defaultValue: {
          type: "any"
        }
      }
    });
    window.queryBus.register(FEATURE_QUERY_ENABLED, {
      type: "string"
    });
    window.eventBus.register(FEATURE_UPDATED, {
      type: "object"
    });
  }
  getFeatures() {
    return __async(this, null, function* () {
      try {
        const features = yield magic.methods.sitefeatures.fetch();
        this.features = this.normalizeFeatures(features);
        this.checkFeatureOverride();
        return this.features;
      } catch (e) {
        console.error(e);
        return {};
      }
    });
  }
  normalizeFeatures(features) {
    return features.reduce((acc, feature) => {
      acc[feature.slug] = feature;
      return acc;
    }, {});
  }
  overrideFeaturesFromQuery(queries) {
    Object.keys(queries).forEach((key) => {
      if (this.features[key]) {
        this.features[key].active = queries[key].active;
        if (queries[key].feature) {
          this.features[key].feature = queries[key].feature;
        }
      }
    });
  }
  isFeatureEnabled(slug) {
    if (!this.features) {
      return false;
    }
    const currentFeature = this.features[slug];
    return !!currentFeature && this.getFeatureValue({ slug }).isApplicable;
  }
  getFeatureValue({
    slug,
    defaultValue = "",
    location
  }) {
    if (!this.features) {
      return {
        isApplicable: false,
        value: null,
        location: []
      };
    }
    const currentFeature = this.features[slug];
    if (!currentFeature) {
      return { isApplicable: false, value: defaultValue, location: [] };
    }
    return {
      isApplicable: this.isApplicable(currentFeature, {
        language: this.language,
        isDesktop: this.deviceService.isDesktop(),
        ipCountry: location || this.countryService.getCookieCountry(),
        user: magic.session.getUser()
      }),
      value: currentFeature.feature,
      location: currentFeature.location
    };
  }
  isApplicable(feature, config) {
    const currLang = config.language;
    const user = config.user;
    const currCountry = config.ipCountry;
    const isDesktop = config.isDesktop;
    if (!feature.active) {
      return false;
    }
    if (feature.languages.length > 0 && !feature.languages.includes(currLang)) {
      return false;
    }
    if (!feature.platform.desktop && isDesktop) {
      return false;
    }
    if (!feature.platform.mobile && !isDesktop) {
      return false;
    }
    if (user) {
      if (feature.countriesExcludes.includes(user.country)) {
        return false;
      }
      if (feature.countries.length > 0 && !feature.countries.includes(user.country)) {
        return false;
      }
    }
    if (feature.locationExcludes.includes(currCountry)) {
      return false;
    }
    if (feature.location.length > 0 && !feature.location.includes(currCountry)) {
      return false;
    }
    if (user) {
      if (feature.depositCount !== null && user.depositCount < feature.depositCount) {
        return false;
      }
      if (feature.affiliates.length > 0 && !feature.affiliates.includes(user.affiliate || "")) {
        return false;
      }
      if (feature.affiliatesExcludes.includes(user.affiliate || "")) {
        return false;
      }
      if (feature.roles.length > 0 && !feature.roles.every((role) => user.roles.includes(role))) {
        return false;
      }
      if (feature.rolesExcludes.some((role) => user.roles.includes(role))) {
        return false;
      }
      if (feature.segments.length > 0 && !feature.segments.includes(user.segment)) {
        return false;
      }
    }
    return true;
  }
  isFeatureActive(slug) {
    if (!this.features) {
      return false;
    }
    const currentFeature = this.features[slug];
    return !!currentFeature && currentFeature.active;
  }
}

export { FeatureService };
